import apiClient from './api';
import { clientId, msalInstance } from "../data/msalConfig";
import { AUTH_ENABLED } from "../data/authConfig";
import { ContractListItem, ContractCreateRequest, ContractUpdateRequest, ContractResponse, AddressSystem, ContractStatus } from '../types/UserRole';

// Utility function to get a valid access token for the API
const getApiAccessToken = async () => {
    if (!AUTH_ENABLED) return '';
    const accounts = msalInstance.getAllAccounts();
    if (!accounts || accounts.length === 0) {
        throw new Error('No accounts found. User may not be logged in.');
    }
    const request = {
        account: accounts[0],
        scopes: [`api://${clientId}/access_as_user`],
    };
    try {
        const response = await msalInstance.acquireTokenSilent(request);
        return response.accessToken;
    } catch (error) {
        console.error('Silent token acquisition failed, falling back to popup.', error);
        const response = await msalInstance.acquireTokenPopup(request);
        return response.accessToken;
    }
};

// Get contracts by address system
export const getContractsByAddressSystem = async (addressSystemId: number): Promise<ContractListItem[]> => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`/api/ContractManagement/contracts/${addressSystemId}`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    return response.data as ContractListItem[];
};

// Get contract by ID
export const getContractById = async (id: number, addressSystemId: number): Promise<ContractResponse> => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`/api/ContractManagement/contract/${id}/${addressSystemId}`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    return response.data as ContractResponse;
};

// Create new contract
export const createContract = async (contract: ContractCreateRequest): Promise<ContractResponse> => {
    const token = await getApiAccessToken();
    const response = await apiClient.post('/api/ContractManagement/contract', contract, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });
    return response.data as ContractResponse;
};

// Update contract
export const updateContract = async (contract: ContractUpdateRequest): Promise<ContractResponse> => {
    const token = await getApiAccessToken();
    const response = await apiClient.put('/api/ContractManagement/contract', contract, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });
    return response.data as ContractResponse;
};

// Delete contract (single contract)
export const deleteContract = async (contractId: number, addressSystemId: number): Promise<void> => {
    const token = await getApiAccessToken();
    await apiClient.delete(`/api/ContractManagement/contract/${contractId}/${addressSystemId}`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
};

// Delete multiple contracts
export const deleteContracts = async (contractIds: number[], addressSystemId?: number): Promise<void> => {
    const promises = contractIds.map(id => deleteContract(id, addressSystemId || 1));
    await Promise.all(promises);
};

// Get all address systems (using contract-specific endpoint)
export const getAllAddressSystems = async (): Promise<AddressSystem[]> => {
    try {
        const token = await getApiAccessToken();
        
        const response = await apiClient.get('/api/ContractDefaultUsers/address-systems', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        
        return response.data as AddressSystem[];
    } catch (error: any) {
        console.error('getAllAddressSystems error:', error);
        throw error;
    }
};

// Get contract statuses by address system
export const getContractStatuses = async (addressSystemId: number): Promise<ContractStatus[]> => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`/api/ContractManagement/contract-statuses/${addressSystemId}`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    
    const statuses = response.data as Array<{
        id: number;
        name: string;
        defaultValue?: number | boolean;
        addressSystemId: number;
        orderInGroup?: number;
    }>;
    
    return statuses.map((status) => ({
        id: status.id,
        name: status.name,
        defaultValue: status.defaultValue === 1 || status.defaultValue === true
    })) as ContractStatus[];
};

// Get template contracts by address system
export const getTemplateContracts = async (addressSystemId: number): Promise<ContractListItem[]> => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`/api/ContractManagement/templates/${addressSystemId}`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    return response.data as ContractListItem[];
};

// Get VAT options by address system
export const getVatOptions = async (addressSystemId: number): Promise<{ id: number; description: string; }[]> => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`/api/ContractManagement/vat-options/${addressSystemId}`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    return response.data as { id: number; description: string; }[];
};

// Get Payment Term options by address system
export const getPaymentTermOptions = async (addressSystemId: number): Promise<{ id: number; description: string; }[]> => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`/api/ContractManagement/payment-term-options/${addressSystemId}`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    return response.data as { id: number; description: string; }[];
};

// Get framework options by contract ID
export const getFrameworkOptions = async (contractId: number): Promise<{ id: number; description: string; valueStr?: string; }[]> => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`/api/ContractManagement/framework-options/${contractId}`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    return response.data as { id: number; description: string; valueStr?: string; }[];
};
